import React, { useState, useEffect } from 'react';
import { adminAPI } from '../api/adminAPI';
import AdminLayout from '../components/AdminLayout';

const EmailConfigPage = () => {
  const [config, setConfig] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [showPassword, setShowPassword] = useState({});
  const [notificationSettings, setNotificationSettings] = useState({
    notificationsEnabled: false,
    smtpConfigured: false
  });

  const [formData, setFormData] = useState({
    provider: 'smtp',
    isActive: false,
    // SMTP
    smtpHost: '',
    smtpPort: 587,
    smtpSecure: true,
    smtpUser: '',
    smtpPassword: '',
    // Resend
    resendApiKey: '',
    // SendGrid
    sendgridApiKey: '',
    // Mailgun
    mailgunApiKey: '',
    mailgunDomain: '',
    // AWS SES
    awsAccessKeyId: '',
    awsSecretAccessKey: '',
    awsRegion: 'us-east-1',
    // Common
    fromEmail: '',
    fromName: 'LionTek Global',
    replyToEmail: '',
    // Rate Limiting
    dailyLimit: 1000,
    hourlyLimit: 100,
    testMode: false
  });

  useEffect(() => {
    fetchConfig();
    fetchStats();
    fetchNotificationSettings();
  }, []);

  const fetchConfig = async () => {
    try {
      setLoading(true);
      const response = await adminAPI.get('/email-config/config');
      setConfig(response.data);
      setFormData({
        ...formData,
        ...response.data,
        // Don't overwrite secrets with empty values from server
        smtpPassword: '',
        resendApiKey: '',
        sendgridApiKey: '',
        mailgunApiKey: '',
        awsSecretAccessKey: ''
      });
    } catch (error) {
      console.error('Error fetching email config:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await adminAPI.get('/email-config/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchNotificationSettings = async () => {
    try {
      const API_URL = process.env.NODE_ENV === 'development' ? 'http://localhost:5001/api' : '/api';
      const response = await fetch(`${API_URL}/admin/notifications/settings`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      const data = await response.json();
      setNotificationSettings(data);
    } catch (error) {
      console.error('Error fetching notification settings:', error);
    }
  };

  const toggleNotifications = async () => {
    try {
      const API_URL = process.env.NODE_ENV === 'development' ? 'http://localhost:5001/api' : '/api';
      const response = await fetch(`${API_URL}/admin/notifications/settings`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          enabled: !notificationSettings.notificationsEnabled
        })
      });
      const data = await response.json();
      if (data.success) {
        setNotificationSettings(prev => ({
          ...prev,
          notificationsEnabled: data.notificationsEnabled
        }));
        alert(data.message);
      }
    } catch (error) {
      console.error('Error toggling notifications:', error);
      alert('Failed to update notification settings');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      
      // Only send secret fields if they have been changed
      const dataToSend = { ...formData };
      if (!dataToSend.smtpPassword) delete dataToSend.smtpPassword;
      if (!dataToSend.resendApiKey) delete dataToSend.resendApiKey;
      if (!dataToSend.sendgridApiKey) delete dataToSend.sendgridApiKey;
      if (!dataToSend.mailgunApiKey) delete dataToSend.mailgunApiKey;
      if (!dataToSend.awsSecretAccessKey) delete dataToSend.awsSecretAccessKey;

      await adminAPI.put('/email-config/config', dataToSend);
      alert('Email configuration saved successfully!');
      fetchConfig();
      fetchStats();
    } catch (error) {
      console.error('Error saving config:', error);
      alert('Failed to save email configuration: ' + (error.response?.data?.details || error.message));
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    const testEmail = prompt('Enter email address to send test email to:');
    if (!testEmail) return;

    try {
      setTesting(true);
      setTestResult(null);
      const response = await adminAPI.post('/email-config/config/test', { testEmail });
      setTestResult(response.data);
      if (response.data.success) {
        alert('Test email sent successfully! Check your inbox.');
      } else {
        alert('Test failed: ' + response.data.message);
      }
    } catch (error) {
      console.error('Error testing config:', error);
      const message = error.response?.data?.message || error.message;
      setTestResult({
        success: false,
        message: 'Test failed: ' + message
      });
      alert('Test failed: ' + message);
    } finally {
      setTesting(false);
    }
  };

  const togglePasswordVisibility = (field) => {
    setShowPassword({ ...showPassword, [field]: !showPassword[field] });
  };

  const providers = [
    { value: 'smtp', label: 'SMTP', icon: '📧' },
    { value: 'resend', label: 'Resend', icon: '⚡' },
    { value: 'sendgrid', label: 'SendGrid', icon: '📮' },
    { value: 'mailgun', label: 'Mailgun', icon: '📬' },
    { value: 'ses', label: 'AWS SES', icon: '☁️' }
  ];

  return (
    <AdminLayout>
      <div className="p-6">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900">Email Configuration</h1>
          <p className="text-gray-600 mt-2">Configure email sending settings for your application</p>
        </div>

        {/* Statistics Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-white p-4 rounded-lg shadow">
              <p className="text-sm text-gray-600">Today's Emails</p>
              <p className="text-2xl font-bold text-blue-600">{stats.todayCount}</p>
              <p className="text-xs text-gray-500 mt-1">{stats.dailyRemaining} remaining</p>
            </div>
            <div className="bg-white p-4 rounded-lg shadow">
              <p className="text-sm text-gray-600">This Hour</p>
              <p className="text-2xl font-bold text-green-600">{stats.hourCount}</p>
              <p className="text-xs text-gray-500 mt-1">{stats.hourlyRemaining} remaining</p>
            </div>
            <div className="bg-white p-4 rounded-lg shadow">
              <p className="text-sm text-gray-600">Daily Limit</p>
              <p className="text-2xl font-bold text-purple-600">{stats.dailyLimit}</p>
            </div>
            <div className="bg-white p-4 rounded-lg shadow">
              <p className="text-sm text-gray-600">Hourly Limit</p>
              <p className="text-2xl font-bold text-orange-600">{stats.hourlyLimit}</p>
            </div>
          </div>
        )}

        {/* Notification Settings Card */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Recruitment Notifications</h3>
              <p className="text-sm text-gray-600 mb-3">
                Automatically send emails to candidates when their application status changes
              </p>
              {!notificationSettings.smtpConfigured && (
                <p className="text-sm text-amber-600 mb-3">
                  ⚠️ Configure and activate an email provider below to enable notifications
                </p>
              )}
              <div className="flex items-center gap-4 text-sm">
                <div className="flex items-center gap-2">
                  <span className={`w-2 h-2 rounded-full ${notificationSettings.smtpConfigured ? 'bg-green-500' : 'bg-gray-300'}`}></span>
                  <span className="text-gray-600">Email Configured</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`w-2 h-2 rounded-full ${notificationSettings.notificationsEnabled ? 'bg-green-500' : 'bg-gray-300'}`}></span>
                  <span className="text-gray-600">Notifications Active</span>
                </div>
              </div>
            </div>
            <button
              onClick={toggleNotifications}
              disabled={!notificationSettings.smtpConfigured}
              className={`px-6 py-3 rounded-lg font-medium transition-colors ${
                notificationSettings.notificationsEnabled
                  ? 'bg-red-600 hover:bg-red-700 text-white'
                  : 'bg-green-600 hover:bg-green-700 text-white'
              } disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              {notificationSettings.notificationsEnabled ? '🔕 Disable Notifications' : '🔔 Enable Notifications'}
            </button>
          </div>
          
          {notificationSettings.notificationsEnabled && (
            <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-800">
                <strong>📧 Active:</strong> Candidates will receive email notifications when moved to: 
                Screening, Shortlisted, Interview Scheduled, Interviewed, Offer, Rejected, or Hired.
              </p>
            </div>
          )}
        </div>

        {/* Configuration Status */}
        {config && (
          <div className={`p-4 rounded-lg mb-6 ${config.isActive ? 'bg-green-50 border border-green-200' : 'bg-yellow-50 border border-yellow-200'}`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <span className="text-2xl">{config.isActive ? '✅' : '⚠️'}</span>
                <div>
                  <p className="font-semibold text-gray-900">
                    {config.isActive ? 'Email Service Active' : 'Email Service Inactive'}
                  </p>
                  <p className="text-sm text-gray-600">
                    {config.isActive 
                      ? `Using ${config.provider.toUpperCase()} provider` 
                      : 'Configure and activate email service to send emails'}
                  </p>
                  {config.lastTestedAt && (
                    <p className="text-xs text-gray-500 mt-1">
                      Last tested: {new Date(config.lastTestedAt).toLocaleString()}
                    </p>
                  )}
                </div>
              </div>
              <button
                onClick={handleTest}
                disabled={testing || !config.isActive}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {testing ? 'Testing...' : '🧪 Send Test Email'}
              </button>
            </div>
          </div>
        )}

        {/* Test Result */}
        {testResult && (
          <div className={`p-4 rounded-lg mb-6 ${testResult.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
            <p className={`font-semibold ${testResult.success ? 'text-green-800' : 'text-red-800'}`}>
              {testResult.success ? '✓ Test Successful' : '✗ Test Failed'}
            </p>
            <p className={`text-sm mt-1 ${testResult.success ? 'text-green-700' : 'text-red-700'}`}>
              {testResult.message}
            </p>
          </div>
        )}

        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow p-6">
            {/* Provider Selection */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-3">Email Provider</label>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                {providers.map(provider => (
                  <button
                    key={provider.value}
                    type="button"
                    onClick={() => setFormData({ ...formData, provider: provider.value })}
                    className={`p-4 border-2 rounded-lg text-center transition-all ${
                      formData.provider === provider.value
                        ? 'border-blue-600 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="text-3xl mb-2">{provider.icon}</div>
                    <div className="text-sm font-medium">{provider.label}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* SMTP Configuration */}
            {formData.provider === 'smtp' && (
              <div className="space-y-4 mb-6 p-4 bg-gray-50 rounded-lg">
                <h3 className="font-semibold text-lg text-gray-900">SMTP Settings</h3>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">SMTP Host *</label>
                    <input
                      type="text"
                      required
                      value={formData.smtpHost}
                      onChange={(e) => setFormData({ ...formData, smtpHost: e.target.value })}
                      placeholder="smtp.gmail.com"
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">SMTP Port *</label>
                    <input
                      type="number"
                      required
                      value={formData.smtpPort}
                      onChange={(e) => setFormData({ ...formData, smtpPort: parseInt(e.target.value) })}
                      placeholder="587"
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">SMTP Username *</label>
                    <input
                      type="text"
                      required
                      value={formData.smtpUser}
                      onChange={(e) => setFormData({ ...formData, smtpUser: e.target.value })}
                      placeholder="your-email@gmail.com"
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">SMTP Password *</label>
                    <div className="relative">
                      <input
                        type={showPassword.smtp ? 'text' : 'password'}
                        value={formData.smtpPassword}
                        onChange={(e) => setFormData({ ...formData, smtpPassword: e.target.value })}
                        placeholder={config?.smtpPasswordSet ? '••••••••  (saved — leave blank to keep)' : 'Enter password'}
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                      />
                      <button
                        type="button"
                        onClick={() => togglePasswordVisibility('smtp')}
                        className="absolute right-3 top-2.5 text-gray-500 hover:text-gray-700"
                      >
                        {showPassword.smtp ? '👁️' : '👁️‍🗨️'}
                      </button>
                    </div>
                  </div>
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="smtpSecure"
                    checked={formData.smtpSecure}
                    onChange={(e) => setFormData({ ...formData, smtpSecure: e.target.checked })}
                    className="mr-2"
                  />
                  <label htmlFor="smtpSecure" className="text-sm text-gray-700">
                    Use TLS/SSL (Recommended for port 587 or 465)
                  </label>
                </div>
              </div>
            )}

            {/* Resend Configuration */}
            {formData.provider === 'resend' && (
              <div className="space-y-4 mb-6 p-4 bg-gray-50 rounded-lg">
                <h3 className="font-semibold text-lg text-gray-900">Resend Settings</h3>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Resend API Key *</label>
                  <div className="relative">
                    <input
                      type={showPassword.resend ? 'text' : 'password'}
                      required={!config?.resendApiKeySet}
                      value={formData.resendApiKey}
                      onChange={(e) => setFormData({ ...formData, resendApiKey: e.target.value })}
                      placeholder={config?.resendApiKeySet ? '••••••••  (key saved — leave blank to keep)' : 're_xxxxxxxxxxxxxxxxxxxx'}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                    <button
                      type="button"
                      onClick={() => togglePasswordVisibility('resend')}
                      className="absolute right-3 top-2.5 text-gray-500 hover:text-gray-700"
                    >
                      {showPassword.resend ? '👁️' : '👁️‍🗨️'}
                    </button>
                  </div>
                  {config?.resendApiKeySet && !formData.resendApiKey && (
                    <p className="text-xs text-green-600 mt-1">✓ API key is saved. Leave blank to keep the existing key.</p>
                  )}
                  <p className="text-xs text-gray-500 mt-1">
                    Get your API key from <span className="font-medium">resend.com/api-keys</span>. Your verified domain must match the From Email below.
                  </p>
                </div>
              </div>
            )}

            {/* SendGrid Configuration */}
            {formData.provider === 'sendgrid' && (
              <div className="space-y-4 mb-6 p-4 bg-gray-50 rounded-lg">
                <h3 className="font-semibold text-lg text-gray-900">SendGrid Settings</h3>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">SendGrid API Key *</label>
                  <input
                    type={showPassword.sendgrid ? 'text' : 'password'}
                    required
                    value={formData.sendgridApiKey}
                    onChange={(e) => setFormData({ ...formData, sendgridApiKey: e.target.value })}
                    placeholder="SG.xxxxxxxxxxxxxxxxxxxxx"
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <p className="text-sm text-blue-600">📝 Note: SendGrid integration coming soon. Use SMTP for now.</p>
              </div>
            )}

            {/* Mailgun Configuration */}
            {formData.provider === 'mailgun' && (
              <div className="space-y-4 mb-6 p-4 bg-gray-50 rounded-lg">
                <h3 className="font-semibold text-lg text-gray-900">Mailgun Settings</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Mailgun API Key *</label>
                    <input
                      type={showPassword.mailgun ? 'text' : 'password'}
                      required
                      value={formData.mailgunApiKey}
                      onChange={(e) => setFormData({ ...formData, mailgunApiKey: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Mailgun Domain *</label>
                    <input
                      type="text"
                      required
                      value={formData.mailgunDomain}
                      onChange={(e) => setFormData({ ...formData, mailgunDomain: e.target.value })}
                      placeholder="mg.yourdomain.com"
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
                <p className="text-sm text-blue-600">📝 Note: Mailgun integration coming soon. Use SMTP for now.</p>
              </div>
            )}

            {/* AWS SES Configuration */}
            {formData.provider === 'ses' && (
              <div className="space-y-4 mb-6 p-4 bg-gray-50 rounded-lg">
                <h3 className="font-semibold text-lg text-gray-900">AWS SES Settings</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">AWS Access Key ID *</label>
                    <input
                      type="text"
                      required
                      value={formData.awsAccessKeyId}
                      onChange={(e) => setFormData({ ...formData, awsAccessKeyId: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">AWS Secret Access Key *</label>
                    <input
                      type={showPassword.aws ? 'text' : 'password'}
                      required
                      value={formData.awsSecretAccessKey}
                      onChange={(e) => setFormData({ ...formData, awsSecretAccessKey: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">AWS Region *</label>
                  <select
                    value={formData.awsRegion}
                    onChange={(e) => setFormData({ ...formData, awsRegion: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="us-east-1">US East (N. Virginia)</option>
                    <option value="us-west-2">US West (Oregon)</option>
                    <option value="eu-west-1">EU (Ireland)</option>
                    <option value="eu-central-1">EU (Frankfurt)</option>
                    <option value="ap-southeast-1">Asia Pacific (Singapore)</option>
                  </select>
                </div>
                <p className="text-sm text-blue-600">📝 Note: AWS SES integration coming soon. Use SMTP for now.</p>
              </div>
            )}

            {/* Common Settings */}
            <div className="space-y-4 mb-6">
              <h3 className="font-semibold text-lg text-gray-900">Email Settings</h3>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">From Email *</label>
                  <input
                    type="email"
                    required
                    value={formData.fromEmail}
                    onChange={(e) => setFormData({ ...formData, fromEmail: e.target.value })}
                    placeholder="noreply@liontekglobal.com"
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">From Name *</label>
                  <input
                    type="text"
                    required
                    value={formData.fromName}
                    onChange={(e) => setFormData({ ...formData, fromName: e.target.value })}
                    placeholder="LionTek"
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Reply-To / Admin Notifications Email *
                  <span className="ml-1 text-xs text-gray-400 font-normal">(contact form alerts sent here)</span>
                </label>
                <input
                  type="email"
                  required
                  value={formData.replyToEmail}
                  onChange={(e) => setFormData({ ...formData, replyToEmail: e.target.value })}
                  placeholder="hello@liontekglobal.com"
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            {/* Rate Limiting */}
            <div className="space-y-4 mb-6">
              <h3 className="font-semibold text-lg text-gray-900">Rate Limiting</h3>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Daily Limit</label>
                  <input
                    type="number"
                    value={formData.dailyLimit}
                    onChange={(e) => setFormData({ ...formData, dailyLimit: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Hourly Limit</label>
                  <input
                    type="number"
                    value={formData.hourlyLimit}
                    onChange={(e) => setFormData({ ...formData, hourlyLimit: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
            </div>

            {/* Activation and Test Mode */}
            <div className="space-y-4 mb-6">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="isActive"
                  checked={formData.isActive}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                  className="mr-2"
                />
                <label htmlFor="isActive" className="text-sm font-medium text-gray-700">
                  Enable Email Service
                </label>
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="testMode"
                  checked={formData.testMode}
                  onChange={(e) => setFormData({ ...formData, testMode: e.target.checked })}
                  className="mr-2"
                />
                <label htmlFor="testMode" className="text-sm text-gray-700">
                  Test Mode (Log emails without sending)
                </label>
              </div>
            </div>

            {/* Submit Button */}
            <div className="flex gap-3">
              <button
                type="submit"
                disabled={saving}
                className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
              >
                {saving ? 'Saving...' : '💾 Save Configuration'}
              </button>
            </div>
          </form>
        )}

        {/* Help Section */}
        <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h3 className="font-semibold text-blue-900 mb-2">📚 Configuration Help</h3>
          <div className="text-sm text-blue-800 space-y-2">
            <p><strong>Resend (Recommended):</strong> Sign up at resend.com, verify your domain, create an API key, and paste it above. Free tier: 3,000 emails/month.</p>
            <p><strong>Gmail SMTP:</strong> Use smtp.gmail.com:587 with your Gmail address and App Password (not regular password)</p>
            <p><strong>Office 365:</strong> Use smtp.office365.com:587 with your Microsoft account credentials</p>
            <p><strong>Testing:</strong> After saving, use the "Send Test Email" button to verify configuration</p>
            <p><strong>Security:</strong> All passwords and API keys are encrypted before storage</p>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default EmailConfigPage;
